import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FC, Fragment, useRef, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import {
  Button,
  FieldRow,
  Flex,
  Modal,
  Spacer,
  StyledFormGroup,
  StyledFormikDatePicker,
  StyledFormSelectFormik,
  ForwardRefStyledFormSelectFormik,
  IStyledFormSelectFormik,
} from '../../../../../components/shared';
import {
  ALL_LEARNER_PROGRESSION_STATUS,
  ALL_STAKEHOLDER_PHASES,
  FILTERED_INSTITUTIONS_SELECT,
  FILTERED_PERSONNEL_POSITIONS,
  FILTERED_SUBJECTS,
} from '../../../../../graphql';
import { Option, OptionNumber, PeopleProfileInstitutionFormType } from '../../../../../utils/types';
import { isEmpty } from '../../../../../utils';

const Actions = styled.div`
  display: flex;
`;

type EditInstitutionModalFormPropsType = {
  data: PeopleProfileInstitutionFormType;
  parentValues: any | null;
  onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void;
  open: boolean;
  viewMode?: boolean;
  isLearner?: boolean;
  allInstitutionTypes?: any;
  allStakeholderTypes?: any;
  allGrades?: any;
};

const peopleProfileInstitutionFormTypeDefault: PeopleProfileInstitutionFormType = {
  id: '',
  designation: '',
  startDate: new Date('1 January 1970'),
  endDate: new Date('31 December 9999'),
  postLevel: undefined,
  primary: false,
  typeId: undefined,
  positionId: undefined,
  positionOther: '',
  phaseIds: [],
  gradeIds: [],
  subjectIds: [],
  institution: undefined,
  progressionStatusId: undefined,
  year: undefined,
  registrationDate: null,
  dateExited: null,
  trackingId: '',
  ageCohort: undefined,
  reasonForTerminationId: undefined,
  linkedToAnInstitution: 'NO',
  linkedToAnOrganisation: 'NO',
  roleId: undefined,
};

const EditInstitutionModalFormPropsTypeDefault: EditInstitutionModalFormPropsType = {
  data: peopleProfileInstitutionFormTypeDefault,
  parentValues: null,
  onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => {},
  open: false,
  viewMode: true,
};

type FormType = PeopleProfileInstitutionFormType & {
  // fields not for saving
  institutionTypeId: Option | undefined;
  institutionId:
    | (Option & {
        id: string;
        name: string;
        emisNumber: string;
        district: {
          id: string;
          name: string;
        } | null;
        province: {
          id: string;
          name: string;
        } | null;
        localMunicipality: {
          id: string;
          name: string;
        } | null;
        communityType: {
          id: string;
          name: string;
        } | null;
      })
    | undefined;
};

const MAP_INSTITUTIONTYPE_TO_ROLETYPE = {
  'Area Office': undefined,
  'Circuit Office': ['Circuit Officials'],
  'Cluster Office': ['Cluster Officials'],
  'District Office': ['District Officials'],
  'ECD Service': ['ECD Staff', 'Practioners'],
  'National Office': ['National Officials'],
  'Provincial Office': ['Provincial Officials'],
  'Regional Office': ['Regional Officials'],
  'Satellite Office': undefined,
  School: ['School Officials', 'Learners'],
  'Service Office': undefined,
  'University	Students': ['Students'],
};

const POST_LEVEL_OPTIONS: OptionNumber[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
];

const validate = (value: any, label: any) => {
  let error;

  if (isEmpty(value) || !value || !(value && value.length)) {
    error = `${label} is required.`;
  }

  return error;
};

const EditInstitutionModalForm: FC<EditInstitutionModalFormPropsType> = props => {
  const refSelectTypeId = useRef<IStyledFormSelectFormik>(null);
  const parentTypeId: Option | undefined =
    props.parentValues && props.parentValues['typeId'] ? props.parentValues['typeId'] : undefined;

  const viewMode = props.viewMode === true;
  const isNew = props.data.id === '';

  const initialValues: FormType = {
    ...props.data,
    institutionTypeId:
      !isNew && props.data.institution && props.data.institution.type
        ? {
            label: props.data.institution.type.name,
            value: props.data.institution.type.id,
          }
        : props.allInstitutionTypes &&
          props.isLearner &&
          isNew &&
          props.allInstitutionTypes.find((b: any) => b.label && b.label.toLowerCase() === 'ecd service'),
    institutionId:
      !isNew && props.data.institution
        ? {
            label: props.data.institution.name,
            value: props.data.institution.id,
            name: props.data.institution.name,
            id: props.data.institution.id,
            emisNumber: props.data.institution.emisNumber,
            district: props.data.institution.district,
            province: null,
            localMunicipality: null,
            communityType: null,
          }
        : undefined,
    ...(isNew && {
      id: 'create' + new Date().getTime().toString(),
      endDate: new Date(new Date().getFullYear(), 11, 31),
      startDate: new Date(new Date().getFullYear(), 0, 1),
      ...(props.isLearner && {
        typeId:
          props.allStakeholderTypes &&
          props.allStakeholderTypes.find((b: any) => b.label && b.label.toLowerCase() === 'learners'),
        year: { label: `${new Date().getFullYear()}`, value: `${new Date().getFullYear()}` },
      }),
    }),
  };

  const validationSchema = yup.object().shape({
    institutionId: yup
      .object()
      .label('Institution')
      .required(),
    typeId: yup
      .object()
      .label('Role')
      .required(),
    // gradeIds: yup
    //   .array()
    //   .label('Grade')
    //   .when('typeId', {
    //     is: typeId => typeId && typeId.label === 'Learners',
    //     then: yup.array().required(),
    //   }),
    startDate: yup
      .date()
      .label('Start Date')
      .required(),
    endDate: yup
      .date()
      .label('End Date')
      .required(),
  });

  return (
    <Fragment>
      <Modal
        open={props.open}
        onClose={() => props.onClose(true, props.data)}
        heading={isNew ? 'Link to Institution' : 'Edit Institution Association'}
        width="800px"
      >
        {props.open && (
          <Flex direction="column" size="1">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={values => {
                const result: PeopleProfileInstitutionFormType = { ...values };
                if (values.institutionId && values.institutionTypeId) {
                  result.institution = {
                    id: values.institutionId.value,
                    name: values.institutionId.label,
                    emisNumber: values.institutionId.emisNumber || '',
                    district: values.institutionId.district
                      ? {
                          id: values.institutionId.district.id,
                          name: values.institutionId.district.name,
                        }
                      : null,
                    type: {
                      id: values.institutionTypeId.value,
                      name: values.institutionTypeId.label,
                    },
                    province: null,
                    localMunicipality: null,
                    communityType: null,
                    districtMunicipality: null,
                  };
                }
                delete result['institutionTypeId'];
                delete result['institutionId'];
                props.onClose(false, result);
              }}
              render={(formikProps: FormikProps<FormType>) => {
                const values = formikProps.values;
                return (
                  <Fragment>
                    <FieldRow>
                      <Field
                        name="institutionTypeId"
                        render={({ field }: FieldProps<FormType>) => (
                          <StyledFormSelectFormik
                            {...field}
                            field={field}
                            readOnly={!isNew || viewMode}
                            label="Institution Type"
                            form={formikProps}
                            options={
                              !isNew && values.institutionTypeId
                                ? [values.institutionTypeId]
                                : props.allInstitutionTypes
                            }
                            mr={0}
                            changeHandler={(option: any) => {
                              if (isNew) {
                                formikProps.setFieldValue('institutionId', undefined);
                              }
                            }}
                          />
                        )}
                      />
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="institutionId"
                          render={({ field }: any) => (
                            <StyledFormSelectFormik
                              {...field}
                              field={field}
                              label="Institution"
                              placeholder={values.institutionTypeId ? 'Select...' : 'Select Institution Type first'}
                              readOnly={!values.institutionTypeId || !isNew || viewMode}
                              form={formikProps}
                              isMulti={false}
                              options={isNew ? undefined : [values.institutionId]}
                              mr={4}
                              query={{
                                query: FILTERED_INSTITUTIONS_SELECT,
                                variables: {
                                  input: {
                                    first: 20,
                                    ...(isNew && {
                                      typeId: values.institutionTypeId ? values.institutionTypeId.value : 'none',
                                    }),
                                    ...(!isNew &&
                                      initialValues.institutionId && {
                                        id: initialValues.institutionId.value,
                                      }),
                                  },
                                },
                                searchTextField: 'input.name',
                                resultItemOptions: {
                                  copyAllItemFields: true,
                                },
                                skip: !isNew,
                              }}
                            />
                          )}
                        />
                      </FieldRow>
                      {values.institutionTypeId &&
                        (values.institutionTypeId.label === 'School' ||
                          values.institutionTypeId.label === 'ECD Service') && (
                          <FieldRow flex={0.5}>
                            <Field
                              name="institutionId.emisNumber"
                              render={({ field }: any) => (
                                <StyledFormGroup
                                  {...field}
                                  field={field}
                                  form={formikProps}
                                  label="EMIS Number"
                                  type="number"
                                  // mr={4}
                                  readOnly={true}
                                />
                              )}
                            />
                          </FieldRow>
                        )}
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="institutionId.district.name"
                          render={({ field }: any) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="District"
                              mr={4}
                              readOnly={true}
                            />
                          )}
                        />
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="primary"
                          render={({ field }: FieldProps<FormType>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="Primary Institution"
                              type="checkbox"
                              readOnly={viewMode}
                              mr={4}
                              defaultChecked={values.primary}
                              onChange={(option: any) => {
                                formikProps.setFieldValue('primary', !values.primary);
                                if (isNew && option && values.institutionTypeId && refSelectTypeId.current) {
                                  const roleTypes: string[] | undefined =
                                    MAP_INSTITUTIONTYPE_TO_ROLETYPE[values.institutionTypeId.label];
                                  if (roleTypes && roleTypes.length > 0) {
                                    const roleName: string =
                                      parentTypeId && roleTypes.find(x => x === parentTypeId.label)
                                        ? parentTypeId.label
                                        : roleTypes[0];
                                    const roleId = refSelectTypeId.current.getOptions().find(r => r.label === roleName);
                                    formikProps.setFieldValue('typeId', roleId);
                                  }
                                }
                              }}
                              wrapperWidth="40px"
                              width="40px"
                            />
                          )}
                        />
                      </FieldRow>
                    </FieldRow>
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          name="typeId"
                          render={({ field }: FieldProps<FormType>) => (
                            <ForwardRefStyledFormSelectFormik
                              {...field}
                              ref={refSelectTypeId}
                              field={field}
                              label="Role"
                              form={formikProps}
                              readOnly={viewMode}
                              options={props.allStakeholderTypes}
                              mr={4}
                            />
                          )}
                        />
                      </FieldRow>
                      {values.typeId && values.typeId.label === 'Learners' && props.isLearner && (
                        <FieldRow flex={0.5}>
                          <Field
                            name="trackingId"
                            render={({ field }: FieldProps<FormType>) => (
                              <StyledFormGroup
                                {...field}
                                field={field}
                                form={formikProps}
                                label="Tracking ID / Admission Number "
                                mr={4}
                              />
                            )}
                          />
                        </FieldRow>
                      )}
                    </FieldRow>
                    {values.typeId && values.typeId.label === 'Learners' && props.isLearner && (
                      <FieldRow>
                        <FieldRow flex={0.5}>
                          <Field
                            render={() => (
                              <StyledFormikDatePicker
                                isSingleDate={true}
                                form={formikProps}
                                label="Registration Date"
                                dateLabel="registrationDate"
                                date={values.registrationDate}
                                clearIcon={null}
                                format={'d-M-yyyy'}
                                readOnly={viewMode}
                                mr={4}
                              />
                            )}
                          />
                        </FieldRow>
                        <FieldRow flex={0.5}>
                          <Field
                            render={() => (
                              <StyledFormikDatePicker
                                isSingleDate={true}
                                form={formikProps}
                                label="Date Exited"
                                dateLabel="dateExited"
                                date={values.dateExited}
                                clearIcon={null}
                                format={'d-M-yyyy'}
                                readOnly={viewMode}
                                mr={4}
                              />
                            )}
                          />
                        </FieldRow>
                      </FieldRow>
                    )}
                    {values.typeId && values.typeId.label !== 'Learners' && (
                      <FieldRow>
                        <FieldRow flex={values.positionId && values.positionId.label === 'Other' ? 0.2 : 0.5}>
                          <Field
                            name="positionId"
                            render={({ field }: FieldProps<FormType>) => (
                              <StyledFormSelectFormik
                                {...field}
                                field={field}
                                label="Personnel Position"
                                form={formikProps}
                                readOnly={viewMode}
                                mr={4}
                                query={{
                                  query: FILTERED_PERSONNEL_POSITIONS,
                                  variables: {
                                    input: {
                                      typeId: values.typeId ? values.typeId.value : 'none',
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </FieldRow>
                        {values.positionId && values.positionId.label === 'Other' && (
                          <FieldRow flex={0.3}>
                            <Field
                              name="positionOther"
                              render={({ field }: FieldProps<FormType>) => (
                                <StyledFormGroup
                                  {...field}
                                  field={field}
                                  form={formikProps}
                                  label="Personnel Position Other"
                                  readOnly={viewMode}
                                  mr={4}
                                />
                              )}
                            />
                          </FieldRow>
                        )}
                        <FieldRow flex={0.5}>
                          <Field
                            name="designation"
                            render={({ field }: FieldProps<FormType>) => (
                              <StyledFormGroup
                                {...field}
                                field={field}
                                form={formikProps}
                                label="Designation"
                                readOnly={viewMode}
                                mr={0}
                              />
                            )}
                          />
                        </FieldRow>
                      </FieldRow>
                    )}
                    {values.typeId && ['School Officials', 'District Officials'].includes(values.typeId.label) && (
                      <Fragment>
                        {values.typeId.label === 'School Officials' && (
                          <FieldRow>
                            <FieldRow flex={0.5}>
                              <Field
                                name="postLevel"
                                render={({ field }: FieldProps<FormType>) => (
                                  <StyledFormSelectFormik
                                    {...field}
                                    field={field}
                                    label="Post Level"
                                    form={formikProps}
                                    options={POST_LEVEL_OPTIONS}
                                    readOnly={viewMode}
                                    mr={4}
                                  />
                                )}
                              />
                            </FieldRow>
                          </FieldRow>
                        )}
                        <FieldRow>
                          <FieldRow flex={0.5}>
                            <Field
                              name="phaseIds"
                              render={({ field }: FieldProps<FormType>) => (
                                <StyledFormSelectFormik
                                  {...field}
                                  field={field}
                                  label="Phases"
                                  form={formikProps}
                                  readOnly={viewMode}
                                  isMulti={true}
                                  mr={4}
                                  query={{
                                    query: ALL_STAKEHOLDER_PHASES,
                                  }}
                                />
                              )}
                            />
                          </FieldRow>
                          <FieldRow flex={0.5}>
                            <Field
                              name="subjectIds"
                              render={({ field }: FieldProps<FormType>) => (
                                <StyledFormSelectFormik
                                  {...field}
                                  field={field}
                                  label="Subjects"
                                  form={formikProps}
                                  isMulti={true}
                                  readOnly={viewMode}
                                  mr={0}
                                  query={{
                                    query: FILTERED_SUBJECTS,
                                    variables: {
                                      input: {
                                        phaseIds:
                                          values.phaseIds && values.phaseIds.length > 0
                                            ? values.phaseIds.map(p => p.value)
                                            : undefined,
                                      },
                                    },
                                    resultItemOptions: {
                                      labelFieldName: 'subjectGradeReference',
                                    },
                                  }}
                                />
                              )}
                            />
                          </FieldRow>
                        </FieldRow>
                      </Fragment>
                    )}
                    {values.typeId && ['Learners'].includes(values.typeId.label) && (
                      <FieldRow>
                        <FieldRow flex={0.33}>
                          <Field
                            name="year"
                            render={({ field }: FieldProps<FormType>) => (
                              <StyledFormSelectFormik
                                {...field}
                                field={field}
                                label="Year"
                                form={formikProps}
                                options={[
                                  { label: '2000', value: '2000' },
                                  { label: '2001', value: '2002' },
                                  { label: '2002', value: '2003' },
                                  { label: '2003', value: '2003' },

                                  { label: '2004', value: '2004' },
                                  { label: '2005', value: '2005' },
                                  { label: '2006', value: '2006' },
                                  { label: '2007', value: '2007' },
                                  { label: '2008', value: '2008' },
                                  { label: '2009', value: '2009' },

                                  { label: '2010', value: '2010' },
                                  { label: '2012', value: '2012' },
                                  { label: '2013', value: '2013' },
                                  { label: '2014', value: '2014' },
                                  { label: '2015', value: '2015' },
                                  { label: '2016', value: '2016' },
                                  { label: '2017', value: '2017' },
                                  { label: '2018', value: '2018' },
                                  { label: '2019', value: '2019' },
                                  { label: '2019', value: '2019' },
                                  { label: '2020', value: '2020' },
                                  { label: '2021', value: '2021' },
                                  { label: '2022', value: '2022' },
                                  { label: '2023', value: '2023' },
                                  { label: '2024', value: '2024' },
                                ]}
                                readOnly={viewMode}
                                changeHandler={(option: any) => {
                                  if (option) {
                                    formikProps.setFieldValue('startDate', new Date(parseInt(option.value), 0, 1));
                                    formikProps.setFieldValue('endDate', new Date(parseInt(option.value), 11, 31));
                                  }
                                }}
                                mr={4}
                              />
                            )}
                          />
                        </FieldRow>
                        <FieldRow flex={0.33}>
                          <Field
                            name="ageCohort"
                            render={({ field }: FieldProps<FormType>) => (
                              <StyledFormSelectFormik
                                {...field}
                                field={field}
                                label="Age Cohort"
                                form={formikProps}
                                options={[
                                  { label: '0 - 5 years', value: '0 - 5 years' },
                                  { label: '0 - 12 months', value: '0 - 12 months' },
                                  { label: '0 - 18 months', value: '0 - 18 months' },
                                  { label: '1 - 2 years', value: '1 - 2 years' },
                                  { label: '18 - 36 months', value: '18 - 36 months' },
                                  { label: '2 - 3 years', value: '2 - 3 years' },
                                  { label: '3 - 4 years', value: '3 - 4 years' },
                                  { label: '4 - 5 years', value: '4 - 5 years' },
                                  { label: '5 - 6 years', value: '5 - 6 years' },
                                ]}
                                readOnly={viewMode}
                                changeHandler={(option: any) => {
                                  if (values.institutionTypeId && values.institutionTypeId.label === 'School') {
                                    if (option) {
                                      let grade;
                                      if (option.value === '0 - 12 months' || option.value === '0 - 18 months') {
                                        grade =
                                          props.allGrades && props.allGrades.find((a: any) => a.label === '00000');
                                        formikProps.setFieldValue('gradeIds', [grade]);
                                      }
                                      if (
                                        option.value === '1 - 2 years' ||
                                        option.value === '18 - 36 months' ||
                                        option.value === '2 - 3 years'
                                      ) {
                                        grade = props.allGrades && props.allGrades.find((a: any) => a.label === '0000');
                                        formikProps.setFieldValue('gradeIds', [grade]);
                                      }
                                      if (option.value === '3 - 4 years') {
                                        grade = props.allGrades && props.allGrades.find((a: any) => a.label === '000');
                                        formikProps.setFieldValue('gradeIds', [grade]);
                                      }
                                      if (option.value === '4 - 5 years') {
                                        grade = props.allGrades && props.allGrades.find((a: any) => a.label === '00');
                                        formikProps.setFieldValue('gradeIds', [grade]);
                                      }
                                      if (option.value === '5 - 6 years') {
                                        grade = props.allGrades && props.allGrades.find((a: any) => a.label === '0');
                                        formikProps.setFieldValue('gradeIds', [grade]);
                                      }
                                    }
                                  }
                                }}
                                mr={4}
                              />
                            )}
                          />
                        </FieldRow>
                        {values.institutionTypeId && values.institutionTypeId.label !== 'ECD Service' && (
                          <FieldRow flex={0.33}>
                            <Field
                              name="gradeIds"
                              render={({ field }: FieldProps<FormType>) => (
                                <StyledFormSelectFormik
                                  {...field}
                                  field={field}
                                  label="Grade"
                                  form={formikProps}
                                  isMulti={false}
                                  readOnly={viewMode}
                                  options={props.allGrades}
                                  mr={4}
                                  // query={{
                                  //   query: ALL_GRADES,
                                  //   resultItemOptions: {
                                  //     labelFieldName: 'value',
                                  //   },
                                  // }}
                                />
                              )}
                              validate={(value: any) => validate(value, 'Grade')}
                            />
                          </FieldRow>
                        )}
                        {!props.isLearner && (
                          <FieldRow flex={0.25}>
                            <Field
                              name="progressionStatusId"
                              render={({ field }: FieldProps<FormType>) => (
                                <StyledFormSelectFormik
                                  {...field}
                                  field={field}
                                  label="Progression Status"
                                  form={formikProps}
                                  isMulti={false}
                                  readOnly={viewMode}
                                  mr={4}
                                  query={{ query: ALL_LEARNER_PROGRESSION_STATUS }}
                                />
                              )}
                            />
                          </FieldRow>
                        )}
                      </FieldRow>
                    )}
                    <FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          render={() => (
                            <StyledFormikDatePicker
                              isSingleDate={true}
                              form={formikProps}
                              label="Start Date"
                              dateLabel="startDate"
                              date={values.startDate}
                              minDate={new Date(1940, 1, 1)}
                              maxDate={values.endDate}
                              clearIcon={null}
                              format={'d-M-yyyy'}
                              readOnly={viewMode}
                              mr={4}
                            />
                          )}
                        />
                      </FieldRow>
                      <FieldRow flex={0.5}>
                        <Field
                          render={() => (
                            <StyledFormikDatePicker
                              isSingleDate={true}
                              form={formikProps}
                              label="End Date"
                              dateLabel="endDate"
                              date={values.endDate}
                              minDate={values.startDate}
                              maxDate={new Date(9999, 12, 31)}
                              clearIcon={null}
                              format={'d-M-yyyy'}
                              readOnly={viewMode}
                              mr={0}
                            />
                          )}
                        />
                      </FieldRow>
                    </FieldRow>
                    <Spacer spacing={15} />
                    <FieldRow justifyContent={'flex-end'}>
                      <Flex direction="row" size="1" justify="flex-end">
                        <Actions>
                          {!viewMode ? (
                            <Fragment>
                              <Button
                                type="button"
                                text="Cancel"
                                mr={3}
                                onClick={() => props.onClose(true, props.data)}
                              />
                              <Button type="button" text="Save" onClick={() => formikProps.handleSubmit()} />
                            </Fragment>
                          ) : (
                            <Fragment>
                              <Button
                                type="button"
                                text="Close"
                                mr={0}
                                onClick={() => props.onClose(true, props.data)}
                              />
                            </Fragment>
                          )}
                        </Actions>
                      </Flex>
                    </FieldRow>
                  </Fragment>
                );
              }}
            />
          </Flex>
        )}
      </Modal>
    </Fragment>
  );
};

export const useEditInstitutionModalForm = (): [
  React.FC,
  (
    data: PeopleProfileInstitutionFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void,
    isLearner?: boolean,
    allInstitutionTypes?: any,
    allStakeholderTypes?: any,
    allGrades?: any
  ) => void
] => {
  const [showModal, setShowModal] = useState<EditInstitutionModalFormPropsType>(
    EditInstitutionModalFormPropsTypeDefault
  );

  const show = (
    data: PeopleProfileInstitutionFormType,
    parentValues: any | null,
    onDone: (cancel: boolean, data: PeopleProfileInstitutionFormType) => void,
    isLearner?: boolean,
    allInstitutionTypes?: any,
    allStakeholderTypes?: any,
    allGrades?: any
  ) => {
    if (!showModal.open) {
      setShowModal({
        data: data,
        parentValues: parentValues,
        open: true,
        isLearner: isLearner,
        allInstitutionTypes,
        allStakeholderTypes,
        allGrades,
        onClose: (cancel: boolean, data: PeopleProfileInstitutionFormType) => {
          onDone(cancel, data);
          setShowModal(EditInstitutionModalFormPropsTypeDefault);
        },
      });
    }
  };

  return [
    () => {
      return <Fragment>{showModal.open && <EditInstitutionModalForm {...showModal} />}</Fragment>;
    },
    show,
  ];
};
